import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, Activity, Send, CheckCircle, XCircle, Clock,
  AlertTriangle, BarChart3, Users, Zap, Database, ArrowRight,
  ChevronLeft, ChevronRight, Filter, Eye, Loader2, Radio,
  TrendingUp, Package, Inbox, MessageSquare, Hash, HardDrive
} from 'lucide-react';
import api from '../../services/api';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Activity },
  { id: 'trace', label: 'Message Trace', icon: Search },
  { id: 'messages', label: 'Pipeline Messages', icon: MessageSquare },
  { id: 'queue', label: 'Ingest Queue', icon: Inbox },
];

const STATUS_COLORS = {
  DELIVRD: 'bg-emerald-100 text-emerald-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  SENT: 'bg-blue-100 text-blue-700',
  submitted: 'bg-blue-100 text-blue-700',
  sent: 'bg-blue-100 text-blue-700',
  PENDING: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  FAILED: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  UNDELIV: 'bg-red-100 text-red-700',
  REJECTD: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-gray-100 text-gray-600',
  BUSY: 'bg-orange-100 text-orange-700',
  active: 'bg-emerald-100 text-emerald-700',
  error: 'bg-red-100 text-red-700',
  processed: 'bg-emerald-100 text-emerald-700',
};

const StatusBadge = ({ status }) => {
  const color = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${color}`}>
      {status || 'UNKNOWN'}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'indigo', sub }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-2 rounded-lg bg-${color}-50`}>
        <Icon className={`w-4 h-4 text-${color}-500`} />
      </div>
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const KafkaSMSPipeline = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [toast, setToast] = useState(null);

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  });

  // Trace state
  const [traceMessageId, setTraceMessageId] = useState('');
  const [traceResult, setTraceResult] = useState(null);
  const [tracing, setTracing] = useState(false);

  // Messages state
  const [messages, setMessages] = useState(null);
  const [msgFilters, setMsgFilters] = useState({ status: '', user_id: '', mobile: '', page: 1 });

  // Queue state
  const [queue, setQueue] = useState(null);
  const [queueFilter, setQueueFilter] = useState('all');
  const [queuePage, setQueuePage] = useState(1);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, statsRes] = await Promise.all([
        api.get('/master-admin/kafka-sms/dashboard'),
        api.get(`/master-admin/kafka-sms/stats?date_from=${dateRange.from}&date_to=${dateRange.to}`),
      ]);
      setDashboard(dashRes.data);
      setStats(statsRes.data);
    } catch (e) {
      showToast('error', 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Trace message
  const traceMessage = async () => {
    if (!traceMessageId.trim()) return;
    setTracing(true);
    setTraceResult(null);
    try {
      const res = await api.get(`/master-admin/kafka-sms/trace?message_id=${traceMessageId.trim()}`);
      setTraceResult(res.data);
      if (!res.data.summary?.found) {
        showToast('error', 'Message not found in pipeline');
      }
    } catch (e) {
      showToast('error', 'Failed to trace message');
    } finally {
      setTracing(false);
    }
  };

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: msgFilters.page,
        page_size: 50,
        date_from: dateRange.from,
        date_to: dateRange.to,
      });
      if (msgFilters.status) params.set('status', msgFilters.status);
      if (msgFilters.user_id) params.set('user_id', msgFilters.user_id);
      if (msgFilters.mobile) params.set('mobile', msgFilters.mobile);
      const res = await api.get(`/master-admin/kafka-sms/messages?${params}`);
      setMessages(res.data);
    } catch (e) {
      showToast('error', 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [msgFilters, dateRange]);

  // Fetch queue
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/master-admin/kafka-sms/ingest-queue?page=${queuePage}&page_size=50&filter=${queueFilter}`);
      setQueue(res.data);
    } catch (e) {
      showToast('error', 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [queuePage, queueFilter]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'messages') fetchMessages();
    else if (activeTab === 'queue') fetchQueue();
  }, [activeTab, fetchDashboard, fetchMessages, fetchQueue]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (activeTab === 'dashboard') fetchDashboard();
      else if (activeTab === 'messages') fetchMessages();
      else if (activeTab === 'queue') fetchQueue();
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchDashboard, fetchMessages, fetchQueue]);

  // ─── DASHBOARD TAB ───
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard icon={Package} label="Total SMS" value={stats?.total || 0} color="indigo" />
        <StatCard icon={Send} label="Submitted" value={stats?.submitted || 0} color="blue" />
        <StatCard icon={CheckCircle} label="Delivered" value={stats?.delivered || 0} color="emerald" />
        <StatCard icon={XCircle} label="Failed" value={stats?.failed || 0} color="red" />
        <StatCard icon={Clock} label="Pending Queue" value={stats?.pending || 0} color="amber" />
        <StatCard icon={Zap} label="Kafka Topics" value={dashboard?.topics?.length || 0} color="purple" />
        <StatCard icon={HardDrive} label="Kafka Memory" value={dashboard?.kafka_memory || '—'} color="slate" sub={dashboard?.kafka_memory_detail || ''} />
      </div>

      {/* Kafka Topics */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
          <Database className="w-5 h-5 text-indigo-500" />
          <h3 className="font-bold text-gray-800">Kafka Topics</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Topic</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Partitions</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Messages</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(dashboard?.topics || []).map((t, i) => (
                <tr key={i} className="hover:bg-gray-50/50">
                  <td className="px-6 py-3 font-mono text-xs font-semibold text-gray-800">{t.name}</td>
                  <td className="px-4 py-3 text-center text-gray-600">{t.partitions}</td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-800">{(t.messages || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status & Channel Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold text-gray-800">Status Breakdown</h3>
          </div>
          <div className="p-3 space-y-1.5">
            {(stats?.status_counts || []).map((sc, i) => {
              const total = stats?.total || 1;
              const pct = ((sc.count / total) * 100).toFixed(1);
              return (
                <div key={i} className="flex items-center gap-2">
                  <StatusBadge status={sc.status} />
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-full rounded-full ${sc.status === 'DELIVRD' || sc.status === 'DELIVERED' ? 'bg-emerald-400' : sc.status === 'FAILED' || sc.status === 'UNDELIV' ? 'bg-red-400' : 'bg-indigo-400'}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-gray-600 w-14 text-right">{sc.count.toLocaleString()}</span>
                  <span className="text-[9px] text-gray-400 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
            {(!stats?.status_counts || stats.status_counts.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-4">No data for selected date range</p>
            )}
          </div>
        </div>

        {/* User Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
            <Users className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold text-gray-800">Top Users</h3>
          </div>
          <div className="p-4 space-y-2">
            {(stats?.user_counts || []).map((uc, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                <span className="flex-1 text-sm font-semibold text-gray-700">{uc.username}</span>
                <span className="text-sm font-bold text-gray-800">{uc.count.toLocaleString()}</span>
              </div>
            ))}
            {(!stats?.user_counts || stats.user_counts.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-4">No data</p>
            )}
          </div>
        </div>
      </div>

      {/* Hourly Chart */}
      {((dashboard?.hourly_counts && dashboard.hourly_counts.length > 0) || (stats?.hourly_counts && stats.hourly_counts.length > 0)) && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold text-gray-800">Hourly SMS Volume (Today)</h3>
          </div>
          <div className="p-6">
            <div className="flex items-end gap-1 h-40">
              {Array.from({ length: 24 }, (_, h) => {
                const hourlyData = dashboard?.hourly_counts || stats?.hourly_counts || [];
                const entry = hourlyData.find(e => e.hour === h);
                const count = entry?.count || 0;
                const maxCount = Math.max(...hourlyData.map(e => e.count), 1);
                const height = (count / maxCount) * 100;
                return (
                  <div key={h} className="flex-1 flex flex-col items-center gap-1 group relative">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {h}:00 — {count.toLocaleString()}
                    </div>
                    <div className={`w-full rounded-t transition-all ${count > 0 ? 'bg-indigo-400 hover:bg-indigo-500' : 'bg-gray-100'}`}
                      style={{ height: `${Math.max(height, 2)}%` }} />
                    <span className="text-[8px] text-gray-400">{h}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ─── TRACE TAB ───
  const renderTrace = () => (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-5 h-5 text-indigo-500" />
          <h3 className="font-bold text-gray-800">Trace SMS by Message ID</h3>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            value={traceMessageId}
            onChange={e => setTraceMessageId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && traceMessage()}
            placeholder="Enter Message ID (e.g. 17716173789352833)"
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
          <button onClick={traceMessage} disabled={tracing || !traceMessageId.trim()}
            className="px-6 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-40 flex items-center gap-2 transition-all">
            {tracing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Trace
          </button>
        </div>
      </div>

      {/* Trace Result */}
      {traceResult && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Hash className="w-4 h-4 text-indigo-500" />
              Message Summary
            </h4>
            {traceResult.summary?.found ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><span className="text-[10px] text-gray-400 uppercase block">Message ID</span><span className="text-xs font-mono font-bold text-gray-800">{traceResult.summary.message_id}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Mobile</span><span className="text-sm font-semibold text-gray-800">{traceResult.summary.mobile}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Entity ID</span><span className="text-xs font-mono font-semibold text-gray-800">{traceResult.summary.entity_id}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Final Status</span><StatusBadge status={traceResult.summary.final_status || traceResult.summary.status} /></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Channel</span><span className="text-sm font-semibold text-gray-800">{traceResult.summary.channel}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">SMS Parts</span><span className="text-sm font-semibold text-gray-800">{traceResult.summary.sms_parts}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Operator</span><span className="text-sm font-semibold text-gray-800">{traceResult.summary.operator || '—'}</span></div>
                <div><span className="text-[10px] text-gray-400 uppercase block">Template ID</span><span className="text-xs font-mono text-gray-700">{traceResult.summary.template_id || '—'}</span></div>
                <div className="col-span-2 md:col-span-4"><span className="text-[10px] text-gray-400 uppercase block">SMS Text</span><span className="text-xs text-gray-700 break-all bg-gray-50 rounded-lg p-2 block mt-1">{traceResult.summary.sms_text}</span></div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Message ID <span className="font-mono font-bold">{traceResult.summary?.message_id}</span> not found in pipeline</p>
              </div>
            )}
          </div>

          {/* Routing Policy */}
          {traceResult.routing_policy && (
            <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-5">
              <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-indigo-500" />
                Routing Policy Applied
              </h4>
              <div className="flex flex-wrap gap-4">
                <div className="bg-indigo-50 rounded-xl px-4 py-2.5">
                  <span className="text-[10px] text-indigo-400 uppercase block font-semibold">Policy</span>
                  <span className="text-sm font-bold text-indigo-700">{traceResult.routing_policy.policy_name}</span>
                </div>
                <div className="bg-indigo-50 rounded-xl px-4 py-2.5">
                  <span className="text-[10px] text-indigo-400 uppercase block font-semibold">Type</span>
                  <span className="text-sm font-bold text-indigo-700">{traceResult.routing_policy.policy_type}</span>
                </div>
                <div className="bg-indigo-50 rounded-xl px-4 py-2.5">
                  <span className="text-[10px] text-indigo-400 uppercase block font-semibold">Priority</span>
                  <span className="text-sm font-bold text-indigo-700">{traceResult.routing_policy.priority}</span>
                </div>
                {traceResult.routing_policy.failover_connector_ids && (
                  <div className="bg-amber-50 rounded-xl px-4 py-2.5">
                    <span className="text-[10px] text-amber-500 uppercase block font-semibold">Failover IDs</span>
                    <span className="text-sm font-bold text-amber-700">{traceResult.routing_policy.failover_connector_ids}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          {traceResult.trace && traceResult.trace.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h4 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Activity className="w-4 h-4 text-indigo-500" />
                Full Pipeline Trace ({traceResult.trace.length} steps)
              </h4>
              <div className="space-y-0">
                {traceResult.trace.map((step, i) => {
                  const isLast = i === traceResult.trace.length - 1;
                  const isSuccess = step.status === 'DELIVRD' || step.status === 'DELIVERED' || step.status === 'delivered' || step.status === 'processed' || step.status === 'submitted' || step.status === 'SUCCESS';
                  const isFailed = step.status === 'FAILED' || step.status === 'failed' || step.status === 'UNDELIV' || step.status === 'REJECTD';
                  const isFailover = step.status === 'FAILOVER';
                  const dotColor = isSuccess ? 'bg-emerald-500' : isFailed ? 'bg-red-500' : isFailover ? 'bg-amber-500' : 'bg-indigo-500';
                  const borderColor = isFailed ? 'border-red-100 bg-red-50/30' : isFailover ? 'border-amber-100 bg-amber-50/30' : isSuccess ? 'border-emerald-100 bg-emerald-50/20' : 'border-gray-100';
                  return (
                    <div key={i} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-4 h-4 rounded-full ${dotColor} ring-4 ring-white shadow-sm flex-shrink-0`} />
                        {!isLast && <div className={`w-0.5 flex-1 ${isFailed ? 'bg-red-200' : isFailover ? 'bg-amber-200' : 'bg-gray-200'} my-1`} />}
                      </div>
                      <div className={`flex-1 pb-6`}>
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-gray-800">{step.stage}</span>
                          <StatusBadge status={step.status} />
                        </div>
                        {step.timestamp && <span className="text-[11px] text-gray-400 block mb-2">{step.timestamp}</span>}
                        {step.details && (
                          <div className={`rounded-xl p-3 space-y-1 border ${borderColor}`}>
                            {Object.entries(step.details).map(([k, v]) => (
                              <div key={k} className="flex gap-2 text-xs">
                                <span className="text-gray-400 font-semibold min-w-[130px] capitalize">{k.replace(/_/g, ' ')}:</span>
                                <span className={`font-mono break-all ${k === 'error' && v && v !== 'ESME_ROK (Success)' ? 'text-red-600 font-semibold' : k === 'action' ? 'text-amber-600 font-semibold' : 'text-gray-700'}`}>{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ─── MESSAGES TAB ───
  const renderMessages = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <Filter className="w-4 h-4 text-gray-400" />
          <select value={msgFilters.status} onChange={e => setMsgFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-200">
            <option value="">All Status</option>
            <option value="DELIVRD">Delivered</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="FAILED">Failed</option>
            <option value="PENDING">Pending</option>
            <option value="UNDELIV">Undelivered</option>
          </select>
          <input type="text" placeholder="User ID" value={msgFilters.user_id}
            onChange={e => setMsgFilters(f => ({ ...f, user_id: e.target.value, page: 1 }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-xs w-24 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <input type="text" placeholder="Mobile" value={msgFilters.mobile}
            onChange={e => setMsgFilters(f => ({ ...f, mobile: e.target.value, page: 1 }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-xs w-32 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <button onClick={fetchMessages} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-xs font-bold hover:bg-indigo-600 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5" /> Search
          </button>
          {messages && <span className="text-[11px] text-gray-400 ml-auto">{messages.total?.toLocaleString()} results</span>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="text-left px-4 py-3 font-semibold text-gray-500 uppercase">Message ID</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Mobile</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Entity ID</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Channel</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 uppercase">Parts</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 uppercase">Final</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Operator TXN</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Created</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 uppercase">Trace</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(messages?.messages || []).map((m, i) => (
                <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-[10px] font-bold text-gray-700 max-w-[140px] truncate">{m.message_id}</td>
                  <td className="px-3 py-2.5 font-semibold text-gray-700">{m.mobile}</td>
                  <td className="px-3 py-2.5 text-gray-600">{m.entity_id}</td>
                  <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[9px] font-bold">{m.channel}</span></td>
                  <td className="px-3 py-2.5 text-center font-bold text-gray-700">{m.sms_parts}</td>
                  <td className="px-3 py-2.5 text-center"><StatusBadge status={m.status} /></td>
                  <td className="px-3 py-2.5 text-center"><StatusBadge status={m.final_status} /></td>
                  <td className="px-3 py-2.5 font-mono text-[10px] text-gray-500 max-w-[100px] truncate">{m.operator_txn_id || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{new Date(m.created_at).toLocaleString('en-GB', { hour12: false, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button onClick={() => { setTraceMessageId(m.message_id); setActiveTab('trace'); setTimeout(traceMessage, 100); }}
                      className="p-1.5 rounded-lg hover:bg-indigo-100 text-indigo-500 transition-colors" title="Trace">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {(!messages?.messages || messages.messages.length === 0) && (
                <tr><td colSpan={10} className="text-center py-10 text-gray-400 text-sm">No messages found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {messages && messages.total_pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-50 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">Page {messages.page} of {messages.total_pages}</span>
            <div className="flex gap-2">
              <button disabled={messages.page <= 1} onClick={() => setMsgFilters(f => ({ ...f, page: f.page - 1 }))}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={messages.page >= messages.total_pages} onClick={() => setMsgFilters(f => ({ ...f, page: f.page + 1 }))}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── QUEUE TAB ───
  const renderQueue = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />
        {['all', 'pending', 'processed'].map(f => (
          <button key={f} onClick={() => { setQueueFilter(f); setQueuePage(1); }}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${queueFilter === f ? 'bg-indigo-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button onClick={fetchQueue} className="ml-auto p-2 rounded-lg hover:bg-gray-100 text-gray-400">
          <RefreshCw className="w-4 h-4" />
        </button>
        {queue && <span className="text-[11px] text-gray-400">{queue.total?.toLocaleString()} items</span>}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="text-left px-4 py-3 font-semibold text-gray-500 uppercase">ID</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">User ID</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Channel</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Created</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 uppercase">Payload</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(queue?.items || []).map((item, i) => (
                <tr key={i} className="hover:bg-indigo-50/30">
                  <td className="px-4 py-2.5 font-mono font-bold text-gray-700">{item.id}</td>
                  <td className="px-3 py-2.5 font-semibold text-gray-700">{item.user_id}</td>
                  <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[9px] font-bold">{item.channel}</span></td>
                  <td className="px-3 py-2.5 text-center"><StatusBadge status={item.processed ? 'processed' : 'pending'} /></td>
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{new Date(item.created_at).toLocaleString('en-GB', { hour12: false, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td className="px-3 py-2.5 font-mono text-[10px] text-gray-500 max-w-[300px] truncate">{item.payload}</td>
                </tr>
              ))}
              {(!queue?.items || queue.items.length === 0) && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">No items in queue</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {queue && queue.total_pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-50 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">Page {queue.page} of {queue.total_pages}</span>
            <div className="flex gap-2">
              <button disabled={queuePage <= 1} onClick={() => setQueuePage(p => p - 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={queuePage >= queue.total_pages} onClick={() => setQueuePage(p => p + 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2 animate-pulse ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {toast.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <Radio className="w-6 h-6 text-white" />
            </div>
            Kafka SMS Pipeline
          </h1>
          <p className="text-sm text-gray-400 mt-1">Monitor SMS flow through Kafka pipeline — ingestion, processing, operator submission & delivery</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range */}
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3 py-2">
            <input type="date" value={dateRange.from} onChange={e => setDateRange(d => ({ ...d, from: e.target.value }))}
              className="text-xs font-semibold text-gray-700 focus:outline-none" />
            <ArrowRight className="w-3 h-3 text-gray-300" />
            <input type="date" value={dateRange.to} onChange={e => setDateRange(d => ({ ...d, to: e.target.value }))}
              className="text-xs font-semibold text-gray-700 focus:outline-none" />
          </div>
          <button onClick={() => setAutoRefresh(!autoRefresh)}
            className={`p-2.5 rounded-xl border transition-all ${autoRefresh ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'}`}
            title={autoRefresh ? 'Auto-refresh ON (10s)' : 'Auto-refresh OFF'}>
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl border border-gray-100 p-1.5 mb-6 shadow-sm overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-indigo-500 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        {loading && <Loader2 className="w-4 h-4 text-indigo-400 animate-spin ml-auto self-center mr-3" />}
      </div>

      {/* Content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'trace' && renderTrace()}
      {activeTab === 'messages' && renderMessages()}
      {activeTab === 'queue' && renderQueue()}
    </div>
  );
};

export default KafkaSMSPipeline;
